<script lang="ts">
	import { format } from 'svelte-i18n';
	import { afterUpdate, beforeUpdate, onDestroy, onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { user_data } from 'src/stores/account/user';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import {
		claimSensorPriorityDevHud,
		sensorPriorityDevForce,
		setSensorPriorityDevHudContext,
	} from 'src/stores/features/dev/sensor-priority-dev-state';
	import { inputStates } from 'src/stores/features/input-states';
	import LastEvents from 'src/components/EventLog/LastEvents.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import SensorReportLine from 'src/components/Sidepanel/DeviceViews/Sensor/SensorReportLine.svelte';
	import { useNavigateToHistoryTab } from 'src/components/Sidepanel/useNavigateToHistoryTab';
	import {
		PriorityGrid,
		PropertyCard,
		PropertyDock,
		capturePropertyCardFlip,
		playPropertyCardFlip,
	} from '@Shelly/shelly-design-system';
	import { isDevModalAvailable, isNonProductionAppBuild } from 'src/helpers/dev_tools';
	import { is_GBLE } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		SENSOR_OVERLAY_COPY,
		SENSOR_PRIORITY_PRIMARY_MIN_HEIGHT_PX,
		buildSensorPriorityLayout,
		getSensorPriorityFamily,
		isSensorReportStale,
		sensorPriorityFlipKey,
		sensorPriorityLeadCard,
		sensorPriorityLeadTopOffset,
		sensorPriorityPaneHeight,
		type SensorPriorityCard,
		type SensorPriorityFamily,
		type SensorPriorityLabel,
	} from 'src/helpers/sensor_priority_layout';
	import { fetchSensorPrioritySpark } from 'src/helpers/sensor_priority_spark';

	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore as Record<string, unknown> | undefined;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore as Record<string, unknown> | undefined;
	$: uiStateStore = getDeviceState(device?.id);
	$: uiState = $uiStateStore;
	$: family = getSensorPriorityFamily(device?.type);
	$: isBle = is_GBLE(device);
	const reporterIdFromStatus = (value: unknown): string | undefined => {
		if (!value || typeof value !== 'object' || !('id' in value)) return undefined;
		const id = (value as { id?: unknown }).id;
		return typeof id === 'string' && id !== '' ? id : undefined;
	};

	$: reporterId = $inputStates?.[device?.id]?.reporter?.id || reporterIdFromStatus(status?.reporter);
	$: hasReporter = Boolean(reporterId && reporterId !== '');
	$: temperatureUnit =
		device?.ht_ui?.temperature_unit ||
		(settings?.ht_ui as { temperature_unit?: string } | undefined)?.temperature_unit ||
		(settings?.sensors as { temperature_unit?: string } | undefined)?.temperature_unit;
	$: layout = family
		? buildSensorPriorityLayout({
				family,
				deviceType: device.type,
				channel: device.channel ?? 0,
				status,
				settings,
				temperatureUnit,
				isBle,
				online: Boolean(status?.online),
				uiStateType: typeof uiState?.type === 'number' ? uiState.type : undefined,
				hasReporter,
				noRecentReports: isBle && isSensorReportStale(status?._updated as number | undefined),
				force: $sensorPriorityDevForce,
			})
		: null;

	let spark: number[] | undefined;
	let sparkKey = '';

	$: primaryMetricId = layout?.cards.find(
		(card) => card.priority === 'primary' && card.kind !== 'status',
	)?.id;

	$: {
		const nextKey = device?.id && primaryMetricId ? `${device.id}:${primaryMetricId}` : '';
		if (nextKey !== sparkKey) {
			sparkKey = nextKey;
			spark = undefined;
			if (nextKey && primaryMetricId) {
				const key = nextKey;
				fetchSensorPrioritySpark(device, primaryMetricId).then((values) => {
					if (sparkKey === key) spark = values;
				});
			}
		}
	}

	let flipHost: HTMLElement;
	let gridHost: HTMLElement;
	let previousFlip = { rects: new Map<string, DOMRect>(), gridHeight: 0 };
	let playedFlipKey = '';
	let flipRaf = 0;
	let sizeObserver: ResizeObserver | undefined;
	let observedLead: HTMLElement | undefined;
	let releaseDevHud: (() => void) | undefined;
	let claimedHudKey: string | undefined;

	const paneEl = (): HTMLElement | undefined => {
		const pane = flipHost?.closest('.scroll-sidepanel, .sidepanel-content-body');
		return pane instanceof HTMLElement ? pane : flipHost;
	};

	const syncLeadOffset = () => {
		if (!flipHost) return;
		const paneHeight = sensorPriorityPaneHeight(flipHost);
		if (paneHeight <= 0) return;
		const lead = sensorPriorityLeadCard(gridHost);
		if (sizeObserver && lead && lead !== observedLead) {
			if (observedLead) sizeObserver.unobserve(observedLead);
			sizeObserver.observe(lead);
			observedLead = lead;
		}
		const offset = sensorPriorityLeadTopOffset(
			paneHeight,
			lead?.offsetHeight || SENSOR_PRIORITY_PRIMARY_MIN_HEIGHT_PX,
		);
		flipHost.style.setProperty('--sensor-priority-lead-offset', `${offset}px`);
	};

	$: showDevHud = Boolean(
		family && isNonProductionAppBuild() && isDevModalAvailable($user_data?.email),
	);

	const syncDevHud = (open: boolean, nextFamily: SensorPriorityFamily | null, ble: boolean) => {
		if (open && nextFamily) {
			const nextKey = `${nextFamily}:${ble ? 'ble' : 'wifi'}`;
			if (!releaseDevHud) {
				releaseDevHud = claimSensorPriorityDevHud(nextFamily, ble);
				claimedHudKey = nextKey;
			} else if (claimedHudKey !== nextKey) {
				setSensorPriorityDevHudContext(nextFamily, ble);
				claimedHudKey = nextKey;
			}
		} else if (releaseDevHud) {
			releaseDevHud();
			releaseDevHud = undefined;
			claimedHudKey = undefined;
		}
	};

	$: syncDevHud(showDevHud, family, isBle);

	$: flipKey = layout ? sensorPriorityFlipKey(layout) : '';
	$: overlayCopy = layout?.overlay ? SENSOR_OVERLAY_COPY[layout.overlay] : null;

	const flipRoot = (): HTMLElement | undefined => {
		const root =
			flipHost?.closest('.sidepanel-content-wrapper') instanceof HTMLElement
				? flipHost.closest('.sidepanel-content-wrapper')
				: flipHost;
		return root instanceof HTMLElement ? root : undefined;
	};

	beforeUpdate(() => {
		const root = flipRoot();
		if (!root || flipKey === playedFlipKey) return;
		previousFlip = capturePropertyCardFlip(root);
	});

	afterUpdate(() => {
		if (sizeObserver) {
			const pane = paneEl();
			if (pane) sizeObserver.observe(pane);
			if (gridHost) sizeObserver.observe(gridHost);
		}
		syncLeadOffset();
		const root = flipRoot();
		if (!root || flipKey === playedFlipKey) return;
		playedFlipKey = flipKey;
		const snapshot = previousFlip;
		previousFlip = { rects: new Map(), gridHeight: 0 };
		if (!snapshot.rects.size) return;
		if (flipRaf) cancelAnimationFrame(flipRaf);
		flipRaf = requestAnimationFrame(() => {
			flipRaf = 0;
			playPropertyCardFlip(root, snapshot);
		});
	});

	onMount(() => {
		sizeObserver = new ResizeObserver(syncLeadOffset);
		const pane = paneEl();
		if (pane) sizeObserver.observe(pane);
		if (gridHost) sizeObserver.observe(gridHost);
		syncLeadOffset();
	});

	onDestroy(() => {
		if (flipRaf) cancelAnimationFrame(flipRaf);
		flipRaf = 0;
		sizeObserver?.disconnect();
		sizeObserver = undefined;
		observedLead = undefined;
		sparkKey = '';
		releaseDevHud?.();
		releaseDevHud = undefined;
		claimedHudKey = undefined;
	});

	const resolveLabel = (label: SensorPriorityLabel): string =>
		capFirstChar(
			$format(label.key, {
				default: label.default,
			}),
		);

	const cardValue = (card: SensorPriorityCard): string =>
		card.valueLabel ? resolveLabel(card.valueLabel) : card.value;

	const { navigateToHistoryTab } = useNavigateToHistoryTab();

	const handleLastEventsClick = (event: CustomEvent<{ originalTimestamp?: number }>) => {
		if (!device.shared && event.detail?.originalTimestamp) {
			navigateToHistoryTab(event.detail.originalTimestamp);
		}
	};
</script>

<DeviceViewStateWrapper
	{device}
	ignoreOfflineState
	hideSlotStaticBottom
	hideSlotFloatingBottom={!overlayCopy}
>
	<div slot="staticTop">
		<DeviceNotificationLabel
			deviceId={device?.id}
			excludeErrors={['gatewayReportOlderThanDay', 'bleDisabled']}
		/>
	</div>

	<div class="sensor-priority-view" slot="content" bind:this={flipHost}>
		{#if layout}
			<div class="sensor-priority-lead-spacer" aria-hidden="true"></div>
			<div class="sensor-priority-main" bind:this={gridHost}>
				<PriorityGrid animate={false}>
					{#each layout.cards as card (card.id)}
						<PropertyCard
							data-property-id={card.id}
							priority={card.priority}
							value={cardValue(card)}
							unit={card.unit}
							label={resolveLabel(card.label)}
							icon={card.icon}
							tone={card.tone}
							stale={card.stale}
							disabled={Boolean(card.disabled || layout.overlay)}
							kind={card.kind}
							accentColor={card.accentColor}
							chart={card.priority === 'primary' && card.id === primaryMetricId
								? spark
								: undefined}
							hideCaption
							data-span={card.span}
						/>
					{/each}
				</PriorityGrid>
			</div>

			<SensorReportLine {device} overlay={layout.overlay} />

			{#if layout.family === 'motion' || layout.family === 'dw'}
				<LastEvents
					class="mt-m"
					{device}
					layout="row"
					stale={Boolean(layout.overlay)}
					disabled={Boolean(layout.overlay)}
					on:click={handleLastEventsClick}
				/>
			{/if}
		{/if}
	</div>

	<svelte:fragment slot="floatingBottom">
		<PropertyDock
			hidden={!overlayCopy}
			role="status"
			label={overlayCopy
				? `${resolveLabel(overlayCopy.value)}. ${resolveLabel(overlayCopy.label)}`
				: $format('label_pinned_device_properties', { default: 'Pinned device properties' })}
		>
			{#if overlayCopy}
				<PropertyCard
					data-property-id="overlay"
					priority="primary"
					kind="status"
					tone="muted"
					value={resolveLabel(overlayCopy.value)}
					label={resolveLabel(overlayCopy.label)}
					icon={overlayCopy.icon}
				/>
			{/if}
		</PropertyDock>
	</svelte:fragment>
</DeviceViewStateWrapper>

<style>
	.sensor-priority-view {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		min-width: 0;
		min-height: 100%;
	}

	.sensor-priority-lead-spacer {
		flex: 0 0 auto;
		height: var(--sensor-priority-lead-offset, 0px);
	}

	.sensor-priority-main {
		flex: 0 0 auto;
		min-width: 0;
	}
</style>
